import cx from "classnames";
import React from "react";

import CommentsButton from "@/mk2/apps/blogs/components/CommentsButton";
import { getArticleTitleAsString } from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import { blogsArticleUrl, blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import TestingPostMenu from "@/mk2/apps/testings/components/TestingPostMenu";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import { Emojify } from "@/mk2/components/Emojify";
import { Link } from "@/mk2/components/Link";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { astToHtml, defaultFilterAdditionalData } from "@/mk2/helpers/article_ast/filters";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import { BlogPostEntity } from "@/mk2/schemas";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";

import styles from "./TesterPost.mscss";

interface OwnProps {
    className?: string;
    post: BlogPostEntity;
    requestUserPermissions: string[];
    location: HistoryLocationWithState;
}

export default class TesterPost extends React.PureComponent<OwnProps> {
    private astToHtmlCache = cacheLast(true);

    public render() {
        const { className, post, requestUserPermissions, location } = this.props;

        // prepare post message body
        let bodyChunks: React.ReactChild[] = null;
        if (post.contentAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                photos: post.photos,
                customStyles: {
                    header2: styles.TesterPost__body__content__header2,
                    header3: styles.TesterPost__body__content__header3,
                    header4: styles.TesterPost__body__content__header4,
                    instagram: styles.TesterPost__body__content__instagram,
                    tiktok: styles.TesterPost__body__content__instagram,
                },
                linkedObjects: post.contentASTAdditionalData
                    ? post.contentASTAdditionalData.linkedObjects
                    : {},
                renderSpecialWikiLinks: false,
                renderLinksWithNofollow: false,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_LINK,
            };
            bodyChunks = this.astToHtmlCache(
                () => astToHtml(post.contentAST, additionalData),
                post.contentAST,
            );
        }

        const hasMenu = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;

        return (
            <div className={cx(styles.TesterPost, className)}>
                <div className={styles.TesterPost__body}>
                    <div className={styles.TesterPost__body__meta}>
                        <Link to={url(blogsProfileUrl, { username: post.author.username })}>
                            <AvatarImg
                                size={24}
                                user={post.author}
                                className={styles.TesterPost__body__meta__avatar}
                            />
                        </Link>
                        <div>
                            <Link
                                to={url(blogsProfileUrl, { username: post.author.username })}
                                className={styles.TesterPost__body__meta__author}
                            >
                                {post.author.username}
                            </Link>
                        </div>
                    </div>

                    <div className={styles.TesterPost__body__titleWrapper}>
                        <h2 className={styles.TesterPost__body__title}>
                            {getArticleTitleAsString(post.titleAST)}
                        </h2>

                        {hasMenu && <TestingPostMenu post={post} />}
                    </div>

                    <div className={styles.TesterPost__body__content}>
                        <Emojify size={17}>{bodyChunks}</Emojify>
                    </div>
                </div>

                <div className={styles.TesterPost__footer}>
                    <div className={styles.TesterPost__footer__likes}>
                        <Like
                            entity="photoblog.post"
                            id={post.id}
                            type={LikeType.IconCount}
                            className={styles.TesterPost__footer__likes__like}
                            location={location}
                        />
                    </div>
                    <div className={styles.TesterPost__footer__comments}>
                        <Link
                            to={url(blogsArticleUrl, {
                                username: post.author.username,
                                articleSlug: post.slug,
                            })}
                        >
                            <CommentsButton
                                className={cx(styles.TesterPost__footer__comments__commentsButton)}
                            />
                        </Link>
                    </div>
                </div>
            </div>
        );
    }
}
